// frontend/src/components/LiveDetailsModal.jsx
// Read-only live score breakdown — period-by-period, plus whatever clock/
// cricket extras the event carries. No betting here; GameCard still owns
// the actual odds pills.
import { X } from 'lucide-react';
import { colors, fontFamily } from '../theme';

function ordinal(n) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

// Raw odds-api period keys (p1, p2, map1, inning1, ft, ot, ap, ...) mean
// different things per sport — p1/p2 is halves for soccer but quarters for
// basketball, so the label has to be sport-aware.
function periodLabel(key, sportKey) {
  const sk = sportKey || '';
  if (key === 'ft') return 'Full Time';
  if (key === 'ot') return 'Overtime';
  if (key === 'ap') return 'Penalties';
  if (key === 'currentgame') return 'Current Game';

  const mapMatch = /^map(\d+)$/.exec(key);
  if (mapMatch) return `Map ${mapMatch[1]}`;

  const inningMatch = /^inning(\d+)$/.exec(key);
  if (inningMatch) return `${ordinal(Number(inningMatch[1]))} Inning`;

  const pMatch = /^p(\d+)$/.exec(key);
  if (pMatch) {
    const n = Number(pMatch[1]);
    if (sk.startsWith('basketball_') || sk === 'aussierules_afl') return `${ordinal(n)} Quarter`;
    if (sk.startsWith('icehockey_')) return `${ordinal(n)} Period`;
    if (sk.startsWith('soccer_') || sk.startsWith('rugbyunion_') || sk.startsWith('rugbyleague_') || sk.startsWith('handball_')) return `${ordinal(n)} Half`;
    if (sk.startsWith('baseball_')) return `${ordinal(n)} Inning`;
    if (sk.startsWith('tennis_') || sk.startsWith('volleyball_') || sk.startsWith('tabletennis_')) return `Set ${n}`;
    return `Period ${n}`;
  }
  return key.toUpperCase();
}

// Natural viewing order: numbered periods first (in order), then FT, OT, AP.
function sortPeriodKeys(keys) {
  const rank = (k) => {
    const m = /^(p|map|inning)(\d+)$/.exec(k);
    if (m) return Number(m[2]);
    if (k === 'ft') return 900;
    if (k === 'ot') return 901;
    if (k === 'ap') return 902;
    return 999;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b));
}

export default function LiveDetailsModal({ game, onClose }) {
  const periods = game.period_scores || {};
  const orderedKeys = sortPeriodKeys(Object.keys(periods));
  const hasCricketStats = orderedKeys.some(k => periods[k]?.wickets || periods[k]?.overs);
  const clock = game.clock;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="relative rounded-lg w-full max-w-md flex flex-col"
        style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-4 flex justify-between items-center flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(to right, rgba(99,41,101,0.3), rgba(50,20,30,0.4))' }}
        >
          <div>
            <div className="flex items-center gap-1.5 font-semibold text-xs" style={{ color: colors.live }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.live }} />
              LIVE
            </div>
            <h2 className="text-lg font-bold mt-0.5" style={{ color: colors.textPrimary }}>
              {game.away_team} @ {game.home_team}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition" style={{ color: colors.textMuted }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Score + clock */}
          <div className="flex flex-col items-center gap-1">
            {game.home_score != null && game.away_score != null && (
              <div className="text-3xl font-bold" style={{ color: colors.textPrimary }}>
                {game.away_score} &ndash; {game.home_score}
              </div>
            )}
            {clock?.statusDetail && (
              <div className="text-sm font-medium" style={{ color: colors.pending }}>
                {clock.statusDetail}
                {clock.running && typeof clock.minute === 'number' && ` · ${clock.minute}'`}
                {typeof clock.injuryTime === 'number' && clock.injuryTime > 0 && ` (+${clock.injuryTime})`}
              </div>
            )}
          </div>

          {/* Period breakdown */}
          {orderedKeys.length > 0 ? (
            <div className="rounded-lg overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)' }}>
              <div
                className="grid text-[11px] font-semibold uppercase tracking-wide px-3 py-2"
                style={{
                  gridTemplateColumns: hasCricketStats ? '1fr 56px 56px 72px' : '1fr 56px 56px',
                  color: colors.textFaint,
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span>Period</span>
                <span className="text-right">Away</span>
                <span className="text-right">Home</span>
                {hasCricketStats && <span className="text-right">Wkts/Overs</span>}
              </div>
              {orderedKeys.map((key) => {
                const p = periods[key];
                if (!p) return null;
                return (
                  <div
                    key={key}
                    className="grid px-3 py-2 text-sm"
                    style={{
                      gridTemplateColumns: hasCricketStats ? '1fr 56px 56px 72px' : '1fr 56px 56px',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      color: colors.textPrimary,
                    }}
                  >
                    <span className="truncate" style={{ color: colors.textMuted }}>{periodLabel(key, game.sport_key)}</span>
                    <span className="text-right font-semibold">{p.away ?? '—'}</span>
                    <span className="text-right font-semibold">{p.home ?? '—'}</span>
                    {hasCricketStats && (
                      <span className="text-right text-xs" style={{ color: colors.textMuted }}>
                        {p.wickets ? `${p.wickets.away ?? '—'}/${p.wickets.home ?? '—'}` : '—'}
                        {p.overs ? ` · ${p.overs.away ?? '—'}-${p.overs.home ?? '—'} ov` : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-sm" style={{ color: colors.textFaint }}>
              No period breakdown available yet for this game.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
